import * as THREE from 'three';
import { Registry } from '../core/Registry.js';
import { applyBoxUV } from '../render/Materials.js';
import { registerBeamPrefabs } from './BeamPrefabs.js';
import { EV } from '../core/EventBus.js';

/**
 * Every level object type lives in this registry. Adding a new gadget means
 * adding one `prefabs.register(...)` call and referencing its key from level
 * data; nothing in the builder, the modes or the HUD has to change.
 */
export const prefabs = new Registry('prefabs');

const _v = new THREE.Vector3();

// ------------------------------------------------------------------ helpers

function boxMesh(size, material, tile = 4) {
  const geo = new THREE.BoxGeometry(size[0], size[1], size[2]);
  applyBoxUV(geo, size[0], size[1], size[2], tile);
  const mesh = new THREE.Mesh(geo, material);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/** Glowing outline around a box. An emissive slab would cover the whole face. */
function edgeGlow(size, colorHex) {
  const geo = new THREE.EdgesGeometry(new THREE.BoxGeometry(size[0], size[1], size[2]));
  const mat = new THREE.LineBasicMaterial({ color: colorHex, toneMapped: false });
  mat.userData.owned = true;
  return new THREE.LineSegments(geo, mat);
}

function trigger(center, half, handlers = {}) {
  return {
    center: new THREE.Vector3().fromArray(center),
    half: new THREE.Vector3().fromArray(half),
    inside: false,
    enabled: true,
    ...handlers,
  };
}

// Small lines carry the same safety-critical text as headings ("YOU CANNOT
// JUMP IT"); 400-weight 32px smeared into the glow at gameplay distances
// (two player reports, 2026-08-20).
const SIGN_FONT = (big) =>
  `${big ? 600 : 500} ${big ? 46 : 36}px ui-monospace, Menlo, Consolas, monospace`;

function signTexture(lines, { accent = '#2ff3ff' } = {}) {
  const c = document.createElement('canvas');
  const ctx = c.getContext('2d');

  // Measure before sizing: a fixed-width canvas silently clips any line
  // longer than itself, and level authors write lines, not pixel widths.
  let maxLine = 0;
  lines.forEach((line, i) => {
    ctx.font = SIGN_FONT(i === 0);
    maxLine = Math.max(maxLine, ctx.measureText(line).width);
  });
  const padX = 44;
  c.width = Math.max(360, Math.ceil(maxLine) + padX * 2);
  c.height = Math.max(128, lines.length * 74 + 60);

  // Resizing the canvas reset the context state, so everything below sets
  // its own fills and fonts.
  ctx.fillStyle = 'rgba(4, 8, 16, 0.72)';
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.strokeStyle = accent;
  ctx.lineWidth = 3;
  ctx.strokeRect(6, 6, c.width - 12, c.height - 12);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  lines.forEach((line, i) => {
    const big = i === 0;
    ctx.font = SIGN_FONT(big);
    ctx.fillStyle = big ? accent : '#cfe6f5';
    ctx.shadowColor = accent;
    ctx.shadowBlur = big ? 22 : 10;
    ctx.fillText(line, c.width / 2, 46 + i * 74);
  });
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

// -------------------------------------------------------------- structure

prefabs.register('box', (def, ctx) => {
  const size = def.s ?? [4, 4, 4];
  const mat = ctx.materials.get(def.m ?? 'plate');
  const mesh = boxMesh(size, mat, def.tile ?? 4);
  mesh.position.fromArray(def.p);
  if (def.r) mesh.rotation.set(
    THREE.MathUtils.degToRad(def.r[0] || 0),
    THREE.MathUtils.degToRad(def.r[1] || 0),
    THREE.MathUtils.degToRad(def.r[2] || 0),
  );

  const portalable = def.portalable ?? (def.m === 'panel' || def.m === 'panel_warm');
  const collider = ctx.world.addBox({
    position: def.p,
    rotation: def.r,
    halfExtents: [size[0] / 2, size[1] / 2, size[2] / 2],
    portalable,
    tags: def.tags ?? [],
  });

  const out = { objects: [mesh], colliders: [collider] };

  // Optional neon outline, purely to read the silhouette at distance.
  if (def.trim) {
    const hex = ctx.materials.get(`glow_${def.trim === true ? 'cyan' : def.trim}`).color.getHex();
    const t = edgeGlow([size[0] * 1.004, size[1] * 1.004, size[2] * 1.004], hex);
    t.position.copy(mesh.position);
    t.quaternion.copy(mesh.quaternion);
    out.objects.push(t);
  }
  return out;
});

prefabs.register('deco', (def, ctx) => {
  // Visual only geometry: pipes, fins, signage backing. No collider at all.
  const size = def.s ?? [1, 1, 1];
  const mesh = boxMesh(size, ctx.materials.get(def.m ?? 'trim'), def.tile ?? 2);
  mesh.position.fromArray(def.p);
  if (def.r) mesh.rotation.set(
    THREE.MathUtils.degToRad(def.r[0] || 0),
    THREE.MathUtils.degToRad(def.r[1] || 0),
    THREE.MathUtils.degToRad(def.r[2] || 0),
  );
  mesh.castShadow = false;
  return { objects: [mesh], colliders: [] };
});

prefabs.register('strip', (def, ctx) => {
  // A neon bar. The cheapest way to light a corridor and steer the eye.
  const size = def.s ?? [6, 0.12, 0.12];
  const color = def.color ?? 'cyan';
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(...size), ctx.materials.get(`glow_${color}`));
  mesh.position.fromArray(def.p);
  if (def.r) mesh.rotation.set(0, THREE.MathUtils.degToRad(def.r[1] || 0), 0);
  const objects = [mesh];
  // Real lights are opt in. Emissive geometry plus bloom reads as lighting for
  // a fraction of the cost, and a level full of point lights blows the shader
  // permutation budget on mobile.
  if (def.light === true && ctx.tier.particles > 0.5) {
    const hex = mesh.material.color.getHex();
    const light = new THREE.PointLight(hex, def.intensity ?? 14, def.range ?? 22, 2);
    light.position.fromArray(def.p);
    objects.push(light);
  }
  return { objects, colliders: [] };
});

prefabs.register('sign', (def, ctx) => {
  const tex = signTexture(def.lines ?? ['SIGN'], { accent: def.accent ?? '#2ff3ff' });
  const aspect = tex.image.width / tex.image.height;
  // World height in metres, per-sign via def.h. The old 1.05 default read
  // fine in the editor and small in play; the texture is vector-crisp at
  // any h, so raising the floor costs nothing.
  const h = def.h ?? 1.35;
  const mat = new THREE.MeshBasicMaterial({
    map: tex, transparent: true, side: THREE.DoubleSide, toneMapped: false, depthWrite: false,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(h * aspect, h), mat);
  mesh.position.fromArray(def.p);
  mesh.rotation.y = THREE.MathUtils.degToRad(def.yaw ?? 0);
  mesh.renderOrder = 1;
  let t = 0;
  return {
    objects: [mesh],
    colliders: [],
    update(dt) {
      t += dt;
      mesh.position.y = def.p[1] + Math.sin(t * 1.1) * 0.06;
      // The idle pulse used to dip to 0.74, dimming the text it exists to
      // sell; hover at 0.9 and breathe just enough to read as alive.
      mat.opacity = 0.9 + Math.sin(t * 2.3) * 0.04;
    },
    dispose() { tex.dispose(); mat.dispose(); },
  };
});

// ------------------------------------------------------------ interactives

prefabs.register('checkpoint', (def, ctx) => {
  const group = new THREE.Group();
  group.position.fromArray(def.p);
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(1.05, 0.06, 8, 40),
    ctx.materials.get('glow_lime'),
  );
  ring.rotation.x = Math.PI / 2;
  ring.position.y = 0.05;
  group.add(ring);
  const beam = new THREE.Mesh(
    new THREE.CylinderGeometry(0.95, 0.95, 4.5, 20, 1, true),
    ctx.materials.get('field_cyan').clone(),
  );
  beam.material.userData.owned = true;
  beam.material.color.set(0x9dff3c);
  beam.material.opacity = 0.12;
  beam.position.y = 2.25;
  group.add(beam);

  let armed = true;
  let t = 0;
  return {
    objects: [group],
    colliders: [],
    role: 'checkpoint',
    triggers: [trigger([def.p[0], def.p[1] + 1.2, def.p[2]], [1.6, 2.0, 1.6], {
      onEnter(c) {
        if (!armed) return;
        armed = false;
        c.setCheckpoint(def.p, def.yaw ?? null);
        beam.material.opacity = 0.26;
        c.bus.emit(EV.CHECKPOINT, { position: def.p });
      },
    })],
    update(dt) {
      t += dt;
      ring.rotation.z += dt * (armed ? 0.6 : 2.2);
      ring.scale.setScalar(1 + Math.sin(t * 2.4) * 0.04);
    },
  };
});

prefabs.register('shard', (def, ctx) => {
  const group = new THREE.Group();
  group.position.fromArray(def.p);
  const core = new THREE.Mesh(
    new THREE.OctahedronGeometry(0.34, 0),
    ctx.materials.get('glow_amber'),
  );
  group.add(core);
  const cage = new THREE.Mesh(
    new THREE.OctahedronGeometry(0.62, 0),
    new THREE.MeshBasicMaterial({
      color: 0xffb020, wireframe: true, transparent: true, opacity: 0.5, toneMapped: false,
    }),
  );
  cage.material.userData.owned = true;
  group.add(cage);
  const halo = new THREE.Sprite(new THREE.SpriteMaterial({
    map: ctx.materials.sprites.glow, color: 0xffb020,
    transparent: true, opacity: 0.75, blending: THREE.AdditiveBlending, depthWrite: false,
  }));
  halo.material.userData.owned = true;
  halo.scale.setScalar(2.6);
  group.add(halo);

  let taken = false;
  let t = Math.random() * 6;
  return {
    objects: [group],
    colliders: [],
    role: 'shard',
    triggers: [trigger(def.p, [1.1, 1.4, 1.1], {
      onEnter(c) {
        if (taken) return;
        taken = true;
        group.visible = false;
        c.bus.emit(EV.PICKUP, { kind: 'shard', position: def.p });
      },
    })],
    update(dt) {
      if (taken) return;
      t += dt;
      core.rotation.y += dt * 1.6;
      core.rotation.x += dt * 0.9;
      cage.rotation.y -= dt * 0.7;
      group.position.y = def.p[1] + Math.sin(t * 1.7) * 0.22;
      halo.material.opacity = 0.55 + Math.sin(t * 3.3) * 0.2;
    },
  };
});

prefabs.register('goal', (def, ctx) => {
  const group = new THREE.Group();
  group.position.fromArray(def.p);
  group.rotation.y = THREE.MathUtils.degToRad(def.yaw ?? 0);

  const frameMat = ctx.materials.get('trim');
  const w = 3.4, h = 4.4, th = 0.35;
  for (const [x, y, sx, sy] of [
    [-w / 2, h / 2, th, h], [w / 2, h / 2, th, h], [0, h, w + th, th],
  ]) {
    const m = new THREE.Mesh(new THREE.BoxGeometry(sx, sy, 0.6), frameMat);
    m.position.set(x, y, 0);
    group.add(m);
  }

  const fieldMat = new THREE.MeshBasicMaterial({
    color: 0x9dff3c, transparent: true, opacity: 0.22,
    blending: THREE.AdditiveBlending, side: THREE.DoubleSide, depthWrite: false, toneMapped: false,
  });
  fieldMat.userData.owned = true;
  const field = new THREE.Mesh(new THREE.PlaneGeometry(w - 0.2, h - 0.2), fieldMat);
  field.position.set(0, h / 2, 0);
  group.add(field);

  const ring = new THREE.Mesh(new THREE.TorusGeometry(2.1, 0.05, 6, 48), ctx.materials.get('glow_lime'));
  ring.position.set(0, h / 2, -0.1);
  group.add(ring);

  const beacon = new THREE.PointLight(0x9dff3c, 26, 30, 2);
  beacon.position.set(0, h / 2, 0.6);
  group.add(beacon);

  let unlocked = def.locked !== true;
  let t = 0;
  const setUnlocked = (v) => {
    unlocked = v;
    const c = v ? 0x9dff3c : 0xff3355;
    fieldMat.color.setHex(c);
    ring.material = ctx.materials.get(v ? 'glow_lime' : 'glow_red');
    beacon.color.setHex(c);
  };
  setUnlocked(unlocked);

  // The gate can be rotated, so the trigger box is the AABB of the rotated
  // opening rather than the unrotated one.
  const rad = THREE.MathUtils.degToRad(def.yaw ?? 0);
  const ca = Math.abs(Math.cos(rad));
  const sa = Math.abs(Math.sin(rad));
  const halfX = ca * (w / 2) + sa * 1.4;
  const halfZ = sa * (w / 2) + ca * 1.4;

  return {
    objects: [group],
    colliders: [],
    role: 'goal',
    api: { setUnlocked, isUnlocked: () => unlocked },
    triggers: [trigger([def.p[0], def.p[1] + h / 2, def.p[2]], [halfX, h / 2, halfZ], {
      onEnter(c) {
        if (!unlocked) { c.bus.emit(EV.TOAST, { text: 'GATE LOCKED', warn: true }); return; }
        c.completeLevel();
      },
    })],
    update(dt, elapsed) {
      t += dt;
      fieldMat.opacity = (unlocked ? 0.2 : 0.12) + Math.sin(t * 3.0) * 0.06;
      ring.scale.setScalar(1 + Math.sin(t * 1.6) * 0.03);
      beacon.intensity = 22 + Math.sin(t * 4.0) * 8;
    },
  };
});

prefabs.register('pad', (def, ctx) => {
  const group = new THREE.Group();
  group.position.fromArray(def.p);
  const disc = new THREE.Mesh(
    new THREE.CylinderGeometry(1.5, 1.7, 0.35, 24),
    ctx.materials.get('trim'),
  );
  group.add(disc);
  const face = new THREE.Mesh(
    new THREE.CircleGeometry(1.35, 28),
    ctx.materials.get('glow_amber'),
  );
  face.rotation.x = -Math.PI / 2;
  face.position.y = 0.19;
  group.add(face);
  const arrow = new THREE.Mesh(
    new THREE.ConeGeometry(0.5, 1.1, 4),
    ctx.materials.get('glow_amber'),
  );
  arrow.position.y = 1.3;
  group.add(arrow);
  const light = new THREE.PointLight(0xffb020, 18, 16, 2);
  light.position.y = 1.0;
  group.add(light);

  const power = def.power ?? 21;
  const dir = new THREE.Vector3().fromArray(def.dir ?? [0, 1, 0]).normalize();
  let cool = 0;
  let t = 0;

  const base = ctx.world.addBox({
    position: [def.p[0], def.p[1] - 0.1, def.p[2]],
    halfExtents: [1.6, 0.2, 1.6],
    portalable: false,
    tags: ['pad'],
  });

  return {
    objects: [group],
    colliders: [base],
    triggers: [trigger([def.p[0], def.p[1] + 1.0, def.p[2]], [1.6, 1.2, 1.6], {
      // Launch anything that steps on it, not just the player: the rival
      // gets a second trigger pass with its own controller shimmed in as
      // c.player. Only pads and lifts opt in; hazards stay player-only.
      anyBody: true,
      onStay(dt2, c) {
        if (cool > 0) return;
        cool = 0.45;
        _v.copy(dir).multiplyScalar(power);
        if (def.additive) c.player.controller.addImpulse(_v, true);
        else c.player.controller.setLaunch(_v);
        c.bus.emit(EV.PLAYER_BOOST, { power });
      },
    })],
    update(dt) {
      t += dt;
      cool = Math.max(0, cool - dt);
      arrow.position.y = 1.1 + ((t * 1.8) % 1) * 0.9;
      arrow.scale.setScalar(1 - ((t * 1.8) % 1) * 0.5);
      light.intensity = 14 + Math.sin(t * 8) * 6;
    },
  };
});

prefabs.register('lift', (def, ctx) => {
  const h = def.h ?? 20;
  const r = def.r ?? 2.2;
  const group = new THREE.Group();
  group.position.set(def.p[0], def.p[1] + h / 2, def.p[2]);
  const col = new THREE.Mesh(
    new THREE.CylinderGeometry(r, r, h, 24, 1, true),
    new THREE.MeshBasicMaterial({
      color: 0x2ff3ff, transparent: true, opacity: 0.1,
      blending: THREE.AdditiveBlending, side: THREE.DoubleSide, depthWrite: false, toneMapped: false,
    }),
  );
  col.material.userData.owned = true;
  group.add(col);

  const RINGS = 6;
  const rings = [];
  for (let i = 0; i < RINGS; i++) {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(r * 0.92, 0.04, 6, 28), ctx.materials.get('glow_cyan'));
    ring.rotation.x = Math.PI / 2;
    group.add(ring);
    rings.push(ring);
  }

  const accel = def.accel ?? 46;
  const maxUp = def.maxUp ?? 26;
  let t = 0;
  return {
    objects: [group],
    colliders: [],
    triggers: [trigger([def.p[0], def.p[1] + h / 2, def.p[2]], [r, h / 2, r], {
      anyBody: true,
      onStay(dt2, c) {
        const v = c.player.controller.velocity;
        v.y = Math.min(maxUp, v.y + accel * dt2);
        c.player.controller.grounded = false;
      },
    })],
    update(dt) {
      t += dt;
      for (let i = 0; i < RINGS; i++) {
        const f = (t * 0.35 + i / RINGS) % 1;
        rings[i].position.y = -h / 2 + f * h;
        rings[i].scale.setScalar(0.8 + f * 0.3);
      }
    },
  };
});

prefabs.register('fizzler', (def, ctx) => {
  const w = def.w ?? 4;
  const h = def.h ?? 5;
  const group = new THREE.Group();
  group.position.set(def.p[0], def.p[1] + h / 2, def.p[2]);
  group.rotation.y = THREE.MathUtils.degToRad(def.yaw ?? 0);

  const fieldMat = new THREE.MeshBasicMaterial({
    color: 0xff8a1f, transparent: true, opacity: 0.2,
    blending: THREE.AdditiveBlending, side: THREE.DoubleSide, depthWrite: false, toneMapped: false,
  });
  fieldMat.userData.owned = true;
  const field = new THREE.Mesh(new THREE.PlaneGeometry(w, h, 1, 8), fieldMat);
  group.add(field);
  for (const s of [-1, 1]) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.3, h + 0.6, 0.5), ctx.materials.get('trim'));
    post.position.x = s * (w / 2 + 0.15);
    group.add(post);
    const bar = new THREE.Mesh(new THREE.BoxGeometry(0.1, h, 0.1), ctx.materials.get('glow_orange'));
    bar.position.x = s * (w / 2);
    group.add(bar);
  }

  let t = 0;
  const yaw = THREE.MathUtils.degToRad(def.yaw ?? 0);
  const half = [Math.abs(Math.cos(yaw)) * w / 2 + 0.4, h / 2, Math.abs(Math.sin(yaw)) * w / 2 + 0.4];
  return {
    objects: [group],
    colliders: [],
    triggers: [trigger([def.p[0], def.p[1] + h / 2, def.p[2]], half, {
      onEnter(c) {
        c.portals.clear();
        c.bus.emit(EV.TOAST, { text: 'PORTALS DISSOLVED', warn: true });
      },
    })],
    update(dt) {
      t += dt;
      fieldMat.opacity = 0.16 + Math.sin(t * 9) * 0.05;
      field.position.x = Math.sin(t * 2.0) * 0.02;
    },
  };
});

prefabs.register('hazard', (def, ctx) => {
  const s = def.s ?? [6, 0.4, 6];
  const group = new THREE.Group();
  group.position.fromArray(def.p);
  const slab = new THREE.Mesh(new THREE.BoxGeometry(s[0], s[1], s[2]), ctx.materials.get('hazard'));
  group.add(slab);
  const glow = new THREE.Mesh(
    new THREE.BoxGeometry(s[0] * 0.98, s[1] + 0.5, s[2] * 0.98),
    ctx.materials.get('field_red'),
  );
  group.add(glow);
  let t = 0;
  return {
    objects: [group],
    colliders: [],
    triggers: [trigger(def.p, [s[0] / 2, s[1] / 2 + 0.6, s[2] / 2], {
      lethal: true,
      onEnter(c) { c.killPlayer('You touched a live grid.'); },
    })],
    update(dt) {
      t += dt;
      glow.material.opacity = 0.2 + Math.sin(t * 7) * 0.1;
      glow.scale.y = 1 + Math.sin(t * 3.1) * 0.08;
    },
  };
});

/**
 * Demo hologram: a looping figure that ACTS OUT a route. Valve fixes
 * comprehension walls with communication, not difficulty (the hole in
 * the glass); ours is UPLINK, where quitters die zero deaths because the
 * fling concept never lands. path: up to 12 waypoints [t, x, y, z],
 * linearly interpolated; a gap of <= 0.05s between waypoints reads as a
 * teleport (no interpolating through the world). Loops with a short
 * hold on the final pose. Purely visual: no collider, no trigger.
 */
prefabs.register('demo', (def) => {
  const path = (def.path ?? []).slice().sort((a, b) => a[0] - b[0]);
  const group = new THREE.Group();
  group.name = 'route-demo';
  const mat = new THREE.MeshBasicMaterial({
    color: 0x2ff3ff, transparent: true, opacity: 0.5,
    blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false,
  });
  mat.userData.owned = true;
  const torso = new THREE.Mesh(new THREE.BoxGeometry(0.55, 1.15, 0.35), mat);
  torso.position.y = 0.95;
  const head = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.32, 0.32), mat);
  head.position.y = 1.75;
  group.add(torso, head);
  const dur = path.length ? path[path.length - 1][0] + 1.5 : 1;
  let t = 0;
  const pos = new THREE.Vector3();
  const prev = new THREE.Vector3();
  return {
    objects: [group],
    colliders: [],
    update(dt) {
      if (path.length < 2) return;
      t = (t + dt) % dur;
      prev.copy(pos);
      let i = 0;
      while (i < path.length - 1 && path[i + 1][0] <= t) i++;
      const a = path[Math.min(i, path.length - 1)];
      const b = path[Math.min(i + 1, path.length - 1)];
      const span = b[0] - a[0];
      const k = span > 0.05 ? Math.min(1, Math.max(0, (t - a[0]) / span)) : 1;
      pos.set(
        a[1] + (b[1] - a[1]) * k,
        a[2] + (b[2] - a[2]) * k,
        a[3] + (b[3] - a[3]) * k,
      );
      group.position.copy(pos);
      const dx = pos.x - prev.x;
      const dz = pos.z - prev.z;
      if (dx * dx + dz * dz > 1e-6) group.rotation.y = Math.atan2(dx, dz);
      mat.opacity = 0.4 + Math.sin(t * 6) * 0.12;
    },
  };
});

prefabs.register('mover', (def, ctx) => {
  const size = def.s ?? [6, 0.8, 6];
  const mesh = boxMesh(size, ctx.materials.get(def.m ?? 'plate'), def.tile ?? 4);
  const from = new THREE.Vector3().fromArray(def.p);
  const to = new THREE.Vector3().fromArray(def.to ?? def.p);
  const period = def.period ?? 6;
  const collider = ctx.world.addBox({
    position: def.p,
    halfExtents: [size[0] / 2, size[1] / 2, size[2] / 2],
    portalable: def.portalable ?? false,
    tags: ['mover'],
  });
  collider.isKinematic = true;

  const trim = edgeGlow([size[0] * 1.01, size[1] * 1.01, size[2] * 1.01], 0xff2fd0);

  let t = def.phase ?? 0;
  const prev = new THREE.Vector3().copy(from);
  return {
    objects: [mesh, trim],
    colliders: [collider],
    update(dt) {
      t += dt;
      const k = 0.5 - 0.5 * Math.cos((t / period) * Math.PI * 2);
      _v.lerpVectors(from, to, k);
      collider.velocity.subVectors(_v, prev).divideScalar(Math.max(dt, 1e-4));
      prev.copy(_v);
      collider.setPosition(_v.x, _v.y, _v.z);
      mesh.position.copy(_v);
      trim.position.copy(_v);
    },
  };
});

prefabs.register('speedgate', (def, ctx) => {
  const w = def.w ?? 12;
  const h = def.h ?? 10;
  const min = def.min ?? 30;
  const yaw = THREE.MathUtils.degToRad(def.yaw ?? 0);
  const group = new THREE.Group();
  group.position.set(def.p[0], def.p[1] + h / 2, def.p[2]);
  group.rotation.y = yaw;

  const fieldMat = new THREE.MeshBasicMaterial({
    color: 0x2ff3ff, transparent: true, opacity: 0.16,
    blending: THREE.AdditiveBlending, side: THREE.DoubleSide, depthWrite: false, toneMapped: false,
  });
  fieldMat.userData.owned = true;
  const field = new THREE.Mesh(new THREE.PlaneGeometry(w, h, 8, 1), fieldMat);
  group.add(field);
  for (const sd of [-1, 1]) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.5, h + 0.8, 0.8), ctx.materials.get('trim'));
    post.position.x = sd * (w / 2 + 0.25);
    group.add(post);
    const bar = new THREE.Mesh(new THREE.BoxGeometry(0.12, h, 0.12), ctx.materials.get('glow_cyan'));
    bar.position.x = sd * (w / 2);
    group.add(bar);
  }

  // The demanded speed, readable from both sides at course pace.
  const label = signTexture([`${min} M/S`], { accent: '#2ff3ff' });
  const labelMat = new THREE.MeshBasicMaterial({
    map: label, transparent: true, depthWrite: false, toneMapped: false, side: THREE.DoubleSide,
  });
  labelMat.userData.owned = true;
  const aspect = label.image.width / label.image.height;
  const plate = new THREE.Mesh(new THREE.PlaneGeometry(1.6 * aspect, 1.6), labelMat);
  plate.position.y = h / 2 + 1.6;
  group.add(plate);

  // World-space normal of the field plane.
  const n = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
  let toastCool = 0;
  let t = 0;
  const half = [
    Math.abs(Math.cos(yaw)) * (w / 2) + Math.abs(Math.sin(yaw)) * 1.4,
    h / 2,
    Math.abs(Math.sin(yaw)) * (w / 2) + Math.abs(Math.cos(yaw)) * 1.4,
  ];
  return {
    objects: [group],
    colliders: [],
    triggers: [trigger([def.p[0], def.p[1] + h / 2, def.p[2]], half, {
      anyBody: true,
      onStay(dt2, c) {
        const ctrl = c.player.controller;
        if (ctrl.velocity.length() >= min) return;   // fast enough: pass
        // Too slow: put the body back on its own side of the plane and
        // reflect the crossing component. Momentum is the toll.
        const side = Math.sign(
          (ctrl.position.x - def.p[0]) * n.x + (ctrl.position.z - def.p[2]) * n.z,
        ) || 1;
        const dist = 1.4 + ctrl.radius;
        ctrl.position.x = def.p[0] + n.x * side * dist + (1 - Math.abs(n.x)) * (ctrl.position.x - def.p[0]);
        ctrl.position.z = def.p[2] + n.z * side * dist + (1 - Math.abs(n.z)) * (ctrl.position.z - def.p[2]);
        const along = ctrl.velocity.x * n.x + ctrl.velocity.z * n.z;
        if (along * side < 0) {
          ctrl.velocity.x -= n.x * along * 1.5;
          ctrl.velocity.z -= n.z * along * 1.5;
        }
        if (toastCool <= 0) {
          toastCool = 1.6;
          c.bus.emit(EV.TOAST, { text: `TOO SLOW\n${min} M/S TO PASS`, warn: true });
        }
      },
    })],
    update(dt) {
      t += dt;
      toastCool = Math.max(0, toastCool - dt);
      fieldMat.opacity = 0.13 + Math.sin(t * 6) * 0.04;
    },
  };
});

prefabs.register('ring', (def, ctx) => {
  const r = def.r ?? 2.6;
  const power = def.power ?? 14;
  const dir = new THREE.Vector3().fromArray(def.dir ?? [0, 0, -1]).normalize();
  const group = new THREE.Group();
  group.position.set(def.p[0], def.p[1], def.p[2]);
  group.lookAt(group.position.clone().add(dir));

  const torus = new THREE.Mesh(new THREE.TorusGeometry(r, 0.14, 8, 40), ctx.materials.get('glow_lime'));
  group.add(torus);
  const inner = new THREE.Mesh(new THREE.TorusGeometry(r * 0.82, 0.05, 6, 40), ctx.materials.get('glow_cyan'));
  group.add(inner);

  let cool = 0;
  let t = 0;
  return {
    objects: [group],
    colliders: [],
    triggers: [trigger([def.p[0], def.p[1], def.p[2]], [r, r, r], {
      anyBody: true,
      onStay(dt2, c) {
        if (cool > 0) return;
        cool = 0.6;
        _v.copy(dir).multiplyScalar(power);
        c.player.controller.addImpulse(_v, false);
        c.bus.emit(EV.PLAYER_BOOST, { power });
      },
    })],
    update(dt) {
      t += dt;
      cool = Math.max(0, cool - dt);
      const pulse = 1 + Math.sin(t * 5) * 0.04;
      torus.scale.setScalar(pulse);
      inner.rotation.z = t * 1.7;
    },
  };
});

prefabs.register('bouncefloor', (def, ctx) => {
  const [w, d] = [def.s?.[0] ?? 10, def.s?.[1] ?? 10];
  const y = def.p[1];
  const rest = def.rest ?? 0.94;

  const plate = new THREE.Mesh(new THREE.BoxGeometry(w, 1.0, d), ctx.materials.get('plate'));
  plate.position.set(def.p[0], y - 0.5, def.p[2]);
  plate.receiveShadow = true;

  const glowMat = new THREE.MeshBasicMaterial({
    color: 0x9dff3c, transparent: true, opacity: 0.5,
    blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false,
  });
  glowMat.userData.owned = true;
  const glow = new THREE.Mesh(new THREE.PlaneGeometry(w * 0.92, d * 0.92), glowMat);
  glow.rotation.x = -Math.PI / 2;
  glow.position.set(def.p[0], y + 0.04, def.p[2]);

  const collider = ctx.world.addBox({
    position: [def.p[0], y - 0.5, def.p[2]],
    halfExtents: [w / 2, 0.5, d / 2],
    portalable: false,
    tags: ['bounce'],
  });

  let t = 0;
  return {
    objects: [plate, glow],
    colliders: [collider],
    // The catch volume floats ABOVE the surface: by the time a falling body
    // touches the plate, collision has already zeroed its speed, so the
    // bounce must happen on the way in. Walkers stroll under the volume's
    // floor untouched (their feet sit below its lower edge).
    triggers: [trigger([def.p[0], y + 1.5, def.p[2]], [w / 2, 1.3, d / 2], {
      anyBody: true,
      onStay(dt2, c) {
        const ctrl = c.player.controller;
        if (ctrl.velocity.y >= -7) return;
        ctrl.velocity.y = Math.min(70, -ctrl.velocity.y * rest);
        ctrl.grounded = false;
        c.bus.emit(EV.PLAYER_BOOST, { power: ctrl.velocity.y });
      },
    })],
    update(dt) {
      t += dt;
      glowMat.opacity = 0.4 + Math.sin(t * 4) * 0.12;
    },
  };
});

/**
 * Shatter panel: speed as a key. Glass that BREAKS when a body arrives
 * faster than `min` along its normal, and blocks like any wall below
 * that. The trick is the bouncefloor lesson: by the time collision code
 * touches the panel the speed is already gone, so the decision happens in
 * a swept trigger shell on the way in, backed by a pre-resolution contact
 * check for frames that cross the shell. Breaking disables the collider
 * before resolution, so momentum is conserved through the glass.
 */
prefabs.register('shatter', (def, ctx) => {
  const s = def.s ?? [6, 5, 0.3];
  const min = def.min ?? 30;
  // The normal is the thin axis; a panel is a wall or a floor by shape.
  const axis = s.indexOf(Math.min(...s));
  const group = new THREE.Group();
  group.position.fromArray(def.p);

  const glassMat = new THREE.MeshBasicMaterial({
    color: 0x9fe8ff, transparent: true, opacity: 0.16, depthWrite: false, toneMapped: false,
  });
  glassMat.userData.owned = true;
  const glass = new THREE.Mesh(new THREE.BoxGeometry(s[0], s[1], s[2]), glassMat);
  group.add(glass);
  const frame = new THREE.LineSegments(
    new THREE.EdgesGeometry(glass.geometry),
    new THREE.LineBasicMaterial({ color: 0x2ff3ff, transparent: true, opacity: 0.8, toneMapped: false }),
  );
  frame.material.userData.owned = true;
  group.add(frame);

  // The threshold, printed on the glass: readability is the contract.
  const tex = signTexture([`${min}`, 'M/S BREAKS IT'], { accent: '#9fe8ff' });
  const labelMat = new THREE.MeshBasicMaterial({
    map: tex, transparent: true, side: THREE.DoubleSide, depthWrite: false, toneMapped: false,
  });
  const lh = Math.min(1.0, s[(axis + 2) % 3] * 0.3);
  const label = new THREE.Mesh(
    new THREE.PlaneGeometry(lh * (tex.image.width / tex.image.height), lh), labelMat);
  if (axis === 2) label.position.z = s[2] / 2 + 0.02;
  else if (axis === 0) { label.rotation.y = Math.PI / 2; label.position.x = s[0] / 2 + 0.02; }
  else { label.rotation.x = -Math.PI / 2; label.position.y = s[1] / 2 + 0.02; }
  group.add(label);

  const collider = ctx.world.addBox({
    position: def.p,
    halfExtents: [s[0] / 2, s[1] / 2, s[2] / 2],
    portalable: false,
    tags: ['shatter'],
  });

  // Burst shards, spawned on break and animated in update.
  const shards = [];
  let broken = false;
  let lastSlow = -10;
  let t = 0;
  const half = [s[0] / 2, s[1] / 2, s[2] / 2];
  half[axis] += 1.0; // the shell reaches out along the normal

  const breakGlass = (v) => {
    if (broken) return;
    broken = true;
    collider.enabled = false;
    glass.visible = false;
    label.visible = false;
    frame.visible = false;
    for (let i = 0; i < 16; i++) {
      const m = new THREE.Mesh(
        new THREE.PlaneGeometry(0.24, 0.24),
        new THREE.MeshBasicMaterial({
          color: 0xbff2ff, transparent: true, opacity: 0.9,
          side: THREE.DoubleSide, depthWrite: false, toneMapped: false,
        }),
      );
      m.material.userData.owned = true;
      m.position.set(
        (Math.random() - 0.5) * s[0] * 0.9,
        (Math.random() - 0.5) * s[1] * 0.9,
        (Math.random() - 0.5) * s[2] * 0.9,
      );
      m.userData.vel = new THREE.Vector3(
        (Math.random() - 0.5) * 6, Math.random() * 5,
        (Math.random() - 0.5) * 6);
      m.userData.vel.setComponent(axis, Math.sign(v) * (4 + Math.random() * 6));
      group.add(m);
      shards.push(m);
    }
    ctx.bus.emit(EV.SHATTER, { position: def.p, impact: Math.abs(v) });
  };

  // The trigger provides the early burst at ordinary speeds; this contact
  // fallback covers a whole shell crossed in one frame, for either actor.
  collider.onContact = (contact, velocity) => {
    if (!velocity || Math.abs(contact.normal.getComponent(axis)) < 0.9) return;
    const v = velocity.getComponent(axis);
    if (v * contact.normal.getComponent(axis) < 0 && Math.abs(v) >= min) breakGlass(v);
  };

  return {
    objects: [group],
    colliders: [collider],
    triggers: [trigger(def.p, half, {
      anyBody: true,
      onStay(dt2, c) {
        if (broken) return;
        const ctrl = c.player.controller;
        const v = [ctrl.velocity.x, ctrl.velocity.y, ctrl.velocity.z][axis];
        if (Math.abs(v) >= min) {
          breakGlass(v);
        } else if (Math.abs(v) > min * 0.4 && t - lastSlow > 1.2) {
          // A solid thump that did not break it teaches the number.
          lastSlow = t;
          c.bus.emit(EV.TOAST, { text: `TOO SLOW\n${min} M/S BREAKS IT`, warn: true });
        }
      },
    })],
    update(dt) {
      t += dt;
      if (!broken) {
        frame.material.opacity = 0.65 + Math.sin(t * 2.1) * 0.15;
        return;
      }
      for (let i = shards.length - 1; i >= 0; i--) {
        const m = shards[i];
        m.position.addScaledVector(m.userData.vel, dt);
        m.userData.vel.y -= 14 * dt;
        m.rotation.x += dt * 5;
        m.rotation.z += dt * 4;
        m.material.opacity -= dt * 1.1;
        if (m.material.opacity <= 0) {
          group.remove(m);
          m.geometry.dispose();
          m.material.dispose();
          shards.splice(i, 1);
        }
      }
    },
    dispose() {
      tex.dispose();
      labelMat.dispose();
      for (const m of shards) { m.geometry.dispose(); m.material.dispose(); }
    },
  };
});

/**
 * Shear beam: a horizontal ion beam between two emitters. Touching it does
 * not kill; it STRIPS horizontal speed back to walk speed, because in a
 * racer the punishment currency is speed, not lives. `spin` (deg/s) sweeps
 * it; the trigger is a conservative AABB over the sweep circle and the
 * exact test is point-to-segment against the live angle, sampled along the
 * frame's motion so no speed steps over a 0.35m beam between frames.
 */
prefabs.register('beam', (def, ctx) => {
  const len = def.len ?? 12;
  const spin = def.spin ?? 0;
  const shock = def.shock ?? 2;
  const yaw0 = THREE.MathUtils.degToRad(def.yaw ?? 0);
  const BEAM_R = 0.35;
  const group = new THREE.Group();
  group.position.fromArray(def.p);
  const arm = new THREE.Group();
  arm.rotation.y = yaw0;
  group.add(arm);

  const coreMat = new THREE.MeshBasicMaterial({
    color: 0xff5a2a, transparent: true, opacity: 0.95,
    blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false,
  });
  coreMat.userData.owned = true;
  const core = new THREE.Mesh(new THREE.BoxGeometry(len, 0.14, 0.14), coreMat);
  arm.add(core);
  const glowMat = new THREE.MeshBasicMaterial({
    color: 0xff8a3c, transparent: true, opacity: 0.22,
    blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false,
  });
  glowMat.userData.owned = true;
  const glow = new THREE.Mesh(new THREE.BoxGeometry(len, 0.42, 0.42), glowMat);
  arm.add(glow);
  for (const sd of [-1, 1]) {
    const emitter = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.7, 0.7), ctx.materials.get('trim'));
    emitter.position.x = sd * (len / 2 + 0.35);
    arm.add(emitter);
    const eye = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.2, 0.2), coreMat);
    eye.position.x = sd * (len / 2 + 0.05);
    arm.add(eye);
  }

  // Static beams get a snug rotated-extent box; spinning ones the circle.
  const reach = len / 2 + 0.8;
  const half = spin ? [reach, 1.9, reach] : [
    Math.abs(Math.cos(yaw0)) * (len / 2) + 0.8, 1.9,
    Math.abs(Math.sin(yaw0)) * (len / 2) + 0.8,
  ];

  const distPtSeg = (px, pz, x0, z0, x1, z1) => {
    const dx = x1 - x0;
    const dz = z1 - z0;
    const tt = Math.max(0, Math.min(1, ((px - x0) * dx + (pz - z0) * dz) / (dx * dx + dz * dz || 1)));
    return Math.hypot(px - (x0 + dx * tt), pz - (z0 + dz * tt));
  };

  let t = 0;
  let lastStay = -10;
  let cool = 0;
  const prev = { x: 0, z: 0 };
  return {
    objects: [group],
    colliders: [],
    triggers: [trigger(def.p, half, {
      anyBody: true,
      onStay(dt2, c) {
        const ctrl = c.player.controller;
        const fresh = t - lastStay > dt2 * 3 + 1e-4;
        lastStay = t;
        if (fresh) { prev.x = ctrl.position.x; prev.z = ctrl.position.z; }
        const px = ctrl.position.x;
        const pz = ctrl.position.z;
        const py = ctrl.position.y;
        const sampleFrom = { x: prev.x, z: prev.z };
        prev.x = px; prev.z = pz;
        if (cool > 0) return;
        // Vertical band first: jumping OVER the beam must be a real out.
        if (def.p[1] < py - 0.3 || def.p[1] > py + 2.0) return;
        const a = yaw0 + THREE.MathUtils.degToRad(spin) * t;
        const ex = Math.cos(a) * (len / 2);
        const ez = -Math.sin(a) * (len / 2);
        const hitR = BEAM_R + ctrl.radius;
        const stepX = px - sampleFrom.x;
        const stepZ = pz - sampleFrom.z;
        const n = 1 + Math.ceil(Math.hypot(stepX, stepZ) / 0.3);
        let hit = false;
        for (let i = 0; i <= n && !hit; i++) {
          const sx = sampleFrom.x + (stepX * i) / n;
          const sz = sampleFrom.z + (stepZ * i) / n;
          hit = distPtSeg(sx, sz,
            def.p[0] - ex, def.p[2] - ez, def.p[0] + ex, def.p[2] + ez) < hitR;
        }
        if (!hit) return;
        cool = 0.8;
        const hSpeed = Math.hypot(ctrl.velocity.x, ctrl.velocity.z);
        const cap = ctrl.walkSpeed;
        if (hSpeed > cap) {
          const k = cap / hSpeed;
          ctrl.velocity.x *= k;
          ctrl.velocity.z *= k;
        }
        // The strip only bites the fast; the shock bites EVERYONE (phone
        // players have no sprint and walk at the cap already).
        if (shock > 0) ctrl.applyShock(shock);
        c.bus.emit(EV.SHEAR, { position: def.p, stripped: Math.max(0, hSpeed - cap) });
        c.bus.emit(EV.TOAST, { text: 'SHEARED\nSYSTEMS SHOCKED', warn: true });
      },
    })],
    update(dt) {
      t += dt;
      cool = Math.max(0, cool - dt);
      if (spin) arm.rotation.y = yaw0 + THREE.MathUtils.degToRad(spin) * t;
      coreMat.opacity = 0.85 + Math.sin(t * 9) * 0.1;
      glowMat.opacity = 0.18 + Math.sin(t * 9 + 1.3) * 0.05;
    },
  };
});

/**
 * Boost strip: propulsion paint. While GROUNDED on it, the body is pushed
 * along `dir` up to `max` m/s, with grip loosened so the push can win
 * against ground friction (26 m/s^2 against full 8.5 friction nets ~3 m/s;
 * the slip is what makes a floor strip actually launch).
 */
prefabs.register('boost', (def, ctx) => {
  const [w, d] = def.s ?? [4, 10];
  const accel = def.accel ?? 26;
  const max = def.max ?? 34;
  const dir = new THREE.Vector3().fromArray(def.dir ?? [0, 0, -1]);
  dir.y = 0;
  dir.normalize();
  const group = new THREE.Group();
  group.position.set(def.p[0], def.p[1] + 0.03, def.p[2]);
  // Local -z (the chevrons' travel) onto the world push direction.
  group.rotation.y = Math.atan2(-dir.x, -dir.z);

  const bedMat = new THREE.MeshBasicMaterial({
    color: 0xff8a1f, transparent: true, opacity: 0.18,
    blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false,
  });
  bedMat.userData.owned = true;
  const bed = new THREE.Mesh(new THREE.PlaneGeometry(w, d), bedMat);
  bed.rotation.x = -Math.PI / 2;
  group.add(bed);
  // Chevrons march along the push direction; motion IS the signage.
  const chevMat = new THREE.MeshBasicMaterial({
    color: 0xffb03c, transparent: true, opacity: 0.7,
    blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false,
    side: THREE.DoubleSide,
  });
  chevMat.userData.owned = true;
  const chevrons = [];
  for (let i = 0; i < 3; i++) {
    const g2 = new THREE.BufferGeometry();
    const hw = Math.min(1.4, w * 0.35);
    g2.setAttribute('position', new THREE.Float32BufferAttribute([
      -hw, 0.02, 0.7, 0, 0.02, -0.7, 0, 0.02, 0, hw, 0.02, 0.7, 0, 0.02, -0.7, 0, 0.02, 0,
    ], 3));
    const m = new THREE.Mesh(g2, chevMat);
    m.userData.k = i / 3;
    group.add(m);
    chevrons.push(m);
  }

  let t = 0;
  return {
    objects: [group],
    colliders: [],
    triggers: [trigger([def.p[0], def.p[1] + 0.6, def.p[2]], [w / 2, 0.9, d / 2], {
      onStay(dt2, c) {
        const ctrl = c.player.controller;
        if (!ctrl.grounded) return;
        ctrl.applySlip(0.15, 1);
        const along = ctrl.velocity.x * dir.x + ctrl.velocity.z * dir.z;
        if (along < max) {
          const add = Math.min(accel * dt2, max - along);
          ctrl.velocity.x += dir.x * add;
          ctrl.velocity.z += dir.z * add;
        }
      },
    })],
    update(dt) {
      t += dt;
      for (const m of chevrons) {
        const k = ((m.userData.k + t * 0.9) % 1);
        m.position.set(0, 0, d / 2 - k * d);
        m.material.opacity = 0.25 + 0.55 * Math.sin(k * Math.PI);
      }
      bedMat.opacity = 0.15 + Math.sin(t * 6) * 0.04;
    },
  };
});

/**
 * Piston crusher: a kinematic head cycling home -> SLAM -> hold -> retract
 * on a fixed period (dwell 45%, slam 10%, hold 15%, retract 30%). Being
 * pinned between the head and anything solid kills; a graze does not: the
 * static trigger covers the whole sweep hull, and the precise test is
 * player-AABB penetration against the head's CURRENT box, which only gets
 * deep when the solver has nowhere left to push you.
 */
prefabs.register('crusher', (def, ctx) => {
  const s = def.s ?? [4, 2, 4];
  const period = def.period ?? 3;
  const from = new THREE.Vector3().fromArray(def.p);
  const to = new THREE.Vector3().fromArray(def.to ?? def.p);
  const head = boxMesh(s, ctx.materials.get(def.m ?? 'panel'), def.tile ?? 2);
  const trim = edgeGlow([s[0] * 1.01, s[1] * 1.01, s[2] * 1.01], 0xff3c2a);
  const collider = ctx.world.addBox({
    position: def.p,
    halfExtents: [s[0] / 2, s[1] / 2, s[2] / 2],
    portalable: false,
    tags: ['crusher'],
  });
  collider.isKinematic = true;
  // The shaft: a strut from the home anchor to the head, stretched live.
  const dirV = to.clone().sub(from);
  const travel = dirV.length();
  const shaftMat = ctx.materials.get('trim');
  const shaft = new THREE.Mesh(new THREE.BoxGeometry(
    Math.min(s[0], s[2]) * 0.3, 1, Math.min(s[0], s[2]) * 0.3), shaftMat);
  if (travel > 1e-3) {
    shaft.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dirV.clone().normalize());
  }

  // Piecewise cycle position: 0 at home, 1 at full extension.
  const phase = def.phase ?? 0;
  const ext = (tt) => {
    const k = ((tt + phase) % period) / period;
    if (k < 0.45) return 0;
    if (k < 0.55) return (k - 0.45) / 0.10;
    if (k < 0.70) return 1;
    return 1 - (k - 0.70) / 0.30;
  };

  const hull = {
    cx: (from.x + to.x) / 2, cy: (from.y + to.y) / 2, cz: (from.z + to.z) / 2,
    hx: Math.abs(to.x - from.x) / 2 + s[0] / 2,
    hy: Math.abs(to.y - from.y) / 2 + s[1] / 2 + 0.4,
    hz: Math.abs(to.z - from.z) / 2 + s[2] / 2,
  };
  let t = 0;
  let cool = 0;
  let gy = null;      // last grounded height seen in-hull (held ~0.5s)
  let gyT = -10;
  const prev = new THREE.Vector3().copy(from);
  const pos = new THREE.Vector3();
  return {
    objects: [head, trim, shaft],
    colliders: [collider],
    triggers: [trigger([hull.cx, hull.cy, hull.cz], [hull.hx, hull.hy, hull.hz], {
      onStay(dt2, c) {
        if (cool > 0) return;
        const ctrl = c.player.controller;
        const px = ctrl.position.x;
        const py = ctrl.position.y;
        const pz = ctrl.position.z;
        const r = ctrl.radius;
        const ox = s[0] / 2 + r * 0.5 - Math.abs(px - pos.x);
        const oz = s[2] / 2 + r * 0.5 - Math.abs(pz - pos.z);
        // Two measured facts drive this shape. The solver squirts a capsule
        // out sideways before raw penetration gets deep (a dead-center
        // stand recorded zero kills on the depth test alone), and during a
        // slam it shoves the body DOWNWARD with the head, so a test against
        // the live position chases a falling target (traced 2026-08-21: the
        // gap held just above any margin all the way through the floor).
        // So the standing space is measured from the last GROUNDED height,
        // held briefly; the depth test stays for horizontal pins.
        if (ctrl.grounded) { gy = gy == null ? py : Math.max(gy, py); gyT = t; }
        if (t - gyT > 0.5) gy = null;
        const headBottom = pos.y - s[1] / 2;
        const squeezed = gy != null && ox > 0 && oz > 0
          && headBottom < gy + 1.5 && collider.velocity.y < -1;
        const oy = s[1] / 2 + 0.9 - Math.abs(py + 0.9 - pos.y);
        const pinned = ox > 0 && oz > 0 && oy > 0 && Math.min(ox, oz, oy) > 0.3;
        if (squeezed || pinned) {
          cool = 1.5;
          c.killPlayer('The piston made its point.');
        }
      },
    })],
    update(dt) {
      t += dt;
      cool = Math.max(0, cool - dt);
      pos.lerpVectors(from, to, ext(t));
      collider.velocity.subVectors(pos, prev).divideScalar(Math.max(dt, 1e-4));
      prev.copy(pos);
      collider.setPosition(pos.x, pos.y, pos.z);
      head.position.copy(pos);
      trim.position.copy(pos);
      // Shaft spans anchor to head, whatever the extension.
      const mid = from.clone().add(pos).multiplyScalar(0.5);
      shaft.position.copy(mid);
      shaft.scale.y = Math.max(0.3, pos.distanceTo(from));
    },
  };
});

/**
 * Sentinel turret: an anti-linger emplacement. With line of sight inside
 * its range the eye charges amber to red; hold still too long and the
 * shot SHEARS your speed (the beam's currency, not a kill). Break line of
 * sight or leave the range and the charge bleeds away.
 */
prefabs.register('sentinel', (def, ctx) => {
  const range = def.range ?? 40;
  const lock = def.lock ?? 1.2;
  const shock = def.shock ?? 3;
  const group = new THREE.Group();
  group.position.fromArray(def.p);
  const mount = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.5, 1.0), ctx.materials.get('trim'));
  mount.position.y = 0.25;
  group.add(mount);
  const headG = new THREE.Group();
  headG.position.y = 1.0;
  group.add(headG);
  const head = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.7, 1.1), ctx.materials.get('panel'));
  headG.add(head);
  const eyeMat = new THREE.MeshBasicMaterial({
    color: 0xffb03c, transparent: true, opacity: 0.9,
    blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false,
  });
  eyeMat.userData.owned = true;
  // Object3D.lookAt aims +z at the target, so the eye sits on the +z face.
  const eye = new THREE.Mesh(new THREE.CircleGeometry(0.16, 12), eyeMat);
  eye.position.z = 0.56;
  headG.add(eye);
  const boltMat = new THREE.MeshBasicMaterial({
    color: 0xff4a2a, transparent: true, opacity: 0,
    blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false,
  });
  boltMat.userData.owned = true;
  const bolt = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.08, 1), boltMat);
  group.add(bolt);
  const _boltDir = new THREE.Vector3(0, 0, 1);

  const world = ctx.world;
  const eyeWorld = new THREE.Vector3(def.p[0], def.p[1] + 1.0, def.p[2]);
  const aim = new THREE.Vector3();
  const dirW = new THREE.Vector3();
  let charge = 0;
  let cool = 0;
  let seen = 0;      // decays; update() uses it to bleed charge when unseen
  return {
    objects: [group],
    colliders: [],
    triggers: [trigger([def.p[0], def.p[1] + range / 2, def.p[2]],
      [range, range / 2 + 2, range], {
        onStay(dt2, c) {
          if (cool > 0) return;
          const ctrl = c.player.controller;
          aim.set(ctrl.position.x, ctrl.position.y + 1.4, ctrl.position.z);
          dirW.subVectors(aim, eyeWorld);
          const dist = dirW.length();
          if (dist > range) return;
          dirW.normalize();
          const hit = world.raycast(eyeWorld, dirW, dist - 0.5);
          if (hit) return;             // cover works
          seen = 0.2;
          charge += dt2;
          if (charge >= lock) {
            charge = 0;
            cool = 2.5;
            const hSpeed = Math.hypot(ctrl.velocity.x, ctrl.velocity.z);
            const cap = ctrl.walkSpeed;
            if (hSpeed > cap) {
              const k = cap / hSpeed;
              ctrl.velocity.x *= k;
              ctrl.velocity.z *= k;
            }
            // A sentinel targets LINGERERS, who are slow by definition: a
            // speed strip alone was imperceptible to them (and to every
            // phone player). The shock is the real shot.
            if (shock > 0) ctrl.applyShock(shock);
            // The visible bolt: eye to target, one flash.
            bolt.position.copy(eyeWorld).add(aim).multiplyScalar(0.5).sub(group.position);
            bolt.scale.z = dist;
            bolt.quaternion.setFromUnitVectors(_boltDir, dirW);
            boltMat.opacity = 0.9;
            c.bus.emit(EV.SHEAR, { position: def.p, stripped: Math.max(0, hSpeed - cap) });
            c.bus.emit(EV.TOAST, { text: 'SENTINEL SHOCK\nSPEED AND JUMP CUT', warn: true });
          }
        },
      })],
    update(dt) {
      seen = Math.max(0, seen - dt);
      cool = Math.max(0, cool - dt);
      if (seen <= 0) charge = Math.max(0, charge - dt * 1.5);
      const f = Math.min(1, charge / lock);
      eyeMat.color.setHex(f > 0.66 ? 0xff2a2a : f > 0.15 ? 0xffb03c : 0x66d8ff);
      eyeMat.opacity = 0.6 + f * 0.4 + (cool > 2.3 ? 0.4 : 0);
      headG.lookAt(aim.x, aim.y, aim.z);
      boltMat.opacity = Math.max(0, boltMat.opacity - dt * 7);
    },
  };
});

/** Ice patch: the ICE daily's numbers, as paint on one stretch of floor. */
prefabs.register('ice', (def, ctx) => {
  const [w, d] = def.s ?? [8, 8];
  const group = new THREE.Group();
  group.position.set(def.p[0], def.p[1] + 0.03, def.p[2]);
  const mat = new THREE.MeshBasicMaterial({
    color: 0x9fd8ff, transparent: true, opacity: 0.22, depthWrite: false, toneMapped: false,
  });
  mat.userData.owned = true;
  const sheet = new THREE.Mesh(new THREE.PlaneGeometry(w, d), mat);
  sheet.rotation.x = -Math.PI / 2;
  group.add(sheet);
  const rim = new THREE.LineSegments(
    new THREE.EdgesGeometry(new THREE.PlaneGeometry(w, d)),
    new THREE.LineBasicMaterial({ color: 0xdaf4ff, transparent: true, opacity: 0.55, toneMapped: false }),
  );
  rim.material.userData.owned = true;
  rim.rotation.x = -Math.PI / 2;
  group.add(rim);
  let t = 0;
  return {
    objects: [group],
    colliders: [],
    triggers: [trigger([def.p[0], def.p[1] + 0.6, def.p[2]], [w / 2, 0.9, d / 2], {
      onStay(dt2, c) {
        const ctrl = c.player.controller;
        // Same numbers as the ICE mutator, scoped to this patch.
        if (ctrl.grounded) ctrl.applySlip(0.12, 0.5);
      },
    })],
    update(dt) {
      t += dt;
      mat.opacity = 0.2 + Math.sin(t * 1.7) * 0.04;
    },
  };
});

/** Acid puddle: touch it and dissolve. A flat, bubbling, unmistakable no. */
prefabs.register('acid', (def, ctx) => {
  const [w, d] = def.s ?? [3, 3];
  const group = new THREE.Group();
  group.position.set(def.p[0], def.p[1] + 0.03, def.p[2]);
  const poolMat = new THREE.MeshBasicMaterial({
    color: 0x2fbf3c, transparent: true, opacity: 0.5, depthWrite: false, toneMapped: false,
  });
  poolMat.userData.owned = true;
  // Two offset ellipses read as a blob, not a tile.
  for (const [ox, oz, sw, sd] of [[0, 0, 1, 1], [w * 0.18, -d * 0.14, 0.7, 0.75]]) {
    const blob = new THREE.Mesh(new THREE.CircleGeometry(0.5, 20), poolMat);
    blob.scale.set(w * sw, d * sd, 1);
    blob.rotation.x = -Math.PI / 2;
    blob.position.set(ox, ox === 0 ? 0 : 0.005, oz);
    group.add(blob);
  }
  const rimMat = new THREE.MeshBasicMaterial({
    color: 0x8fff5c, transparent: true, opacity: 0.7,
    blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false,
  });
  rimMat.userData.owned = true;
  const bubbles = [];
  for (let i = 0; i < 5; i++) {
    const m = rimMat.clone();   // own fade phase per bubble
    m.userData.owned = true;
    const b = new THREE.Mesh(new THREE.CircleGeometry(0.07, 8), m);
    b.rotation.x = -Math.PI / 2;
    b.userData.seed = Math.random();
    group.add(b);
    bubbles.push(b);
  }
  let t = 0;
  return {
    objects: [group],
    colliders: [],
    triggers: [trigger([def.p[0], def.p[1] + 0.35, def.p[2]], [w / 2, 0.55, d / 2], {
      lethal: true,
      onEnter(c) { c.killPlayer('The acid dissolved you.'); },
    })],
    update(dt) {
      t += dt;
      poolMat.opacity = 0.45 + Math.sin(t * 2.3) * 0.07;
      for (const b of bubbles) {
        const k = (b.userData.seed + t * 0.35) % 1;
        b.position.set(
          Math.sin(b.userData.seed * 40 + Math.floor(b.userData.seed + t * 0.35)) * w * 0.3,
          0.02 + k * 0.25,
          Math.cos(b.userData.seed * 70) * d * 0.3,
        );
        b.material.opacity = 0.7 * (1 - k);
      }
    },
  };
});

prefabs.register('gravfield', (def, ctx) => {
  const [w, h, d] = def.s ?? [12, 30, 12];
  const scale = def.scale ?? 0.28;
  const group = new THREE.Group();
  group.position.set(def.p[0], def.p[1] + h / 2, def.p[2]);

  const mat = new THREE.MeshBasicMaterial({
    color: 0xb28aff, transparent: true, opacity: 0.09,
    blending: THREE.AdditiveBlending, side: THREE.BackSide, depthWrite: false, toneMapped: false,
  });
  mat.userData.owned = true;
  const vol = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  group.add(vol);

  // Rising motes sell "up is cheaper in here" before physics proves it.
  const N = 26;
  const pts = new Float32Array(N * 3);
  for (let i = 0; i < N; i++) {
    pts[i * 3] = (Math.random() - 0.5) * w;
    pts[i * 3 + 1] = (Math.random() - 0.5) * h;
    pts[i * 3 + 2] = (Math.random() - 0.5) * d;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pts, 3));
  const moteMat = new THREE.PointsMaterial({
    color: 0xcfa8ff, size: 0.16, transparent: true, opacity: 0.8,
    blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true,
  });
  moteMat.userData.owned = true;
  const motes = new THREE.Points(geo, moteMat);
  group.add(motes);

  let t = 0;
  return {
    objects: [group],
    colliders: [],
    triggers: [trigger([def.p[0], def.p[1] + h / 2, def.p[2]], [w / 2, h / 2, d / 2], {
      anyBody: true,
      onStay(dt2, c) {
        // The hold must survive until the NEXT trigger test: at low frame
        // rates a fixed hold expires inside a single frame and the field
        // loses its grip exactly when a hitching phone needs it most.
        c.player.controller.setGravityScale(scale, Math.max(0.15, dt2 * 2.5));
      },
    })],
    update(dt) {
      t += dt;
      const arr = geo.attributes.position.array;
      for (let i = 0; i < N; i++) {
        arr[i * 3 + 1] += dt * 1.6;
        if (arr[i * 3 + 1] > h / 2) arr[i * 3 + 1] = -h / 2;
      }
      geo.attributes.position.needsUpdate = true;
      mat.opacity = 0.07 + Math.sin(t * 2.2) * 0.02;
    },
  };
});

prefabs.register('wind', (def, ctx) => {
  const [w, h, d] = def.s ?? [12, 12, 30];
  const dir = new THREE.Vector3().fromArray(def.dir ?? [0, 0, -1]).normalize();
  const accel = def.accel ?? 18;
  const max = def.max ?? 26;
  const group = new THREE.Group();
  group.position.set(def.p[0], def.p[1] + h / 2, def.p[2]);

  const mat = new THREE.MeshBasicMaterial({
    color: 0x9adfff, transparent: true, opacity: 0.05,
    blending: THREE.AdditiveBlending, side: THREE.BackSide, depthWrite: false, toneMapped: false,
  });
  mat.userData.owned = true;
  group.add(new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat));

  // Streaks flowing along the push direction sell the current before the
  // physics does.
  const N = 34;
  const pts = new Float32Array(N * 3);
  for (let i = 0; i < N; i++) {
    pts[i * 3] = (Math.random() - 0.5) * w;
    pts[i * 3 + 1] = (Math.random() - 0.5) * h;
    pts[i * 3 + 2] = (Math.random() - 0.5) * d;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pts, 3));
  const streakMat = new THREE.PointsMaterial({
    color: 0xcfefff, size: 0.22, transparent: true, opacity: 0.7,
    blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true,
  });
  streakMat.userData.owned = true;
  group.add(new THREE.Points(geo, streakMat));

  // Push in LOCAL volume space so wrap-around stays inside the box.
  const local = dir.clone();
  const span = new THREE.Vector3(w, h, d);
  let t = 0;
  return {
    objects: [group],
    colliders: [],
    triggers: [trigger([def.p[0], def.p[1] + h / 2, def.p[2]], [w / 2, h / 2, d / 2], {
      anyBody: true,
      onStay(dt2, c) {
        const v = c.player.controller.velocity;
        // Accelerate along the current, capped: integrating accel * dt keeps
        // the push identical at any frame rate, and the cap means riding the
        // wind converges on the same speed for everyone.
        const along = v.dot(dir);
        if (along < max) {
          const gain = Math.min(accel * dt2, max - along);
          v.addScaledVector(dir, gain);
        }
      },
    })],
    update(dt) {
      t += dt;
      const arr = geo.attributes.position.array;
      for (let i = 0; i < N; i++) {
        arr[i * 3] += local.x * dt * 14;
        arr[i * 3 + 1] += local.y * dt * 14;
        arr[i * 3 + 2] += local.z * dt * 14;
        for (let a = 0; a < 3; a++) {
          const half = span.getComponent(a) / 2;
          if (arr[i * 3 + a] > half) arr[i * 3 + a] = -half;
          if (arr[i * 3 + a] < -half) arr[i * 3 + a] = half;
        }
      }
      geo.attributes.position.needsUpdate = true;
      mat.opacity = 0.04 + Math.sin(t * 3) * 0.015;
    },
  };
});

prefabs.register('spawnpoint', (def) => ({ objects: [], colliders: [], data: { spawn: def } }));

registerBeamPrefabs(prefabs);
